import _ from 'lodash';
import { ChangeDetectorRef, Component, DestroyRef, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { CampaignService } from 'app/campaign/services/campaign.service';
import { Observable, Subject } from 'rxjs';
import { selectDialogState } from 'app/dialog/features/dialog.selectors';
import { ImageService } from 'app/core/services/image.service';
import { DomSanitizer } from '@angular/platform-browser';
import { PeerToPeerPercentageOptionSchema } from 'app/campaign/structure/peer-to-peer';
import { filter } from 'rxjs/operators';
import { ToastService } from 'app/toast/services/toast.service';
import { ChooseImageModalService } from 'app/core/services/choose-image-modal.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InputFileComponent } from '../../input-file/input-file.component';
import { CommonModule } from '@angular/common';
import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import { MdbpInputComponent } from '../../mdb-input/mdb-input.component';
import { MdbTextareaComponent } from '../../mdb-textarea/mdb-textarea.component';
import { InputMaskModule } from '@ngneat/input-mask';
import { QuillCustomComponent } from '../../quill-custom/quill-custom.component';
import { NgxCurrencyDirective } from 'ngx-currency';
import { IconDeleteComponent } from 'app/icon/components/icon-delete/icon-delete.component';

@Component({
  standalone: true,
  imports:[
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputFileComponent,
    MdbpInputComponent,
    MdbFormsModule,
    MdbTextareaComponent,
    InputMaskModule,
    NgxCurrencyDirective,
    QuillCustomComponent, 
    IconDeleteComponent,
  ],
  selector: 'app-p2p-percent-option',
  templateUrl: './p2p-percent-option.component.html',
  styleUrls: ['./p2p-percent-option.component.scss']
})
export class P2pPercentOptionComponent implements OnInit, OnDestroy {
  destroyRef = inject(DestroyRef)
  public dialog$: Observable<any> = this.store.pipe(select(selectDialogState));

  public option: FormGroup = null;
  public takeUntilSubject: Subject<any> = new Subject();
  public images;
  public isNameInvalid = false;
  public isPercentInvalid = false;

  onClose: any;
  @Input()data

  constructor(
    private store: Store,
    private formBuilder: FormBuilder,
    private campaignService: CampaignService,
    public imageService: ImageService,
    private domSanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private toastService: ToastService,
    private chooseImageModalService: ChooseImageModalService,
  ) { 
    this.option = this.formBuilder.group({});
    this.campaignService.generateForm(this.option, PeerToPeerPercentageOptionSchema);
  }

  ngOnInit(): void {

    if(this.data){
      this.campaignService.initializeForm(this.option, this.data, PeerToPeerPercentageOptionSchema);
      this.option.get('imageDataUrl').setValue(this.data.image)
    }
    
    this.option.get('imageFile').valueChanges.pipe(
      filter(value => !_.isNull(value)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      this.images = value;
      const windowURL = window.URL || window.webkitURL;

      if (value && value.length) {
        value.forEach(image => {
          const url = windowURL.createObjectURL(image);
          this.imageService.cropper({
            url: this.domSanitizer.bypassSecurityTrustUrl(url),
            id: 'percent-option-image',
            name: image.name,
            type: image.type,
          });
        });
      } else {
        const url = windowURL.createObjectURL(value);
        this.imageService.cropper({
          url: this.domSanitizer.bypassSecurityTrustUrl(url),
          id: 'percent-option-image',
          name: value.name,
          type: value.type,
        });
      }
    });

    this.imageService.imageDataUrlSubject.pipe(
      filter(({ id }) => id === 'percent-option-image'),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ result }) => {
      this.option.get('imageDataUrl').setValue(result);
    });

    this.imageService.imageSubject.pipe(
      filter(({ id }) => id === 'percent-option-image'),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ result }) => {
      if(result === false) {
        this.resetImage();
      } else {
        this.option.get('image').setValue(result.imageUrl);
      }

      this.cdr.markForCheck();
    });

    this.option.get('percent').valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(()=>{
      this.isPercentInvalid = false;
      this.option.get('percent').setErrors(null);
    })

    this.option.get('name').valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(()=>{
      this.isNameInvalid = false;
      this.option.get('name').setErrors(null);
    })
  }

  close(status) {
    this.onClose({status});
  }
  
  confirm(status, option) {
    let isValid = this.validate(option);
    if(isValid){
      this.onClose({status, option});
    }
  }

  validate(option){
    if(!option.name){
      this.option.get('name').setErrors({'incorrect': true})
      this.isNameInvalid = true;
      return false
    }

    if(!option.percent || option.percent < 1){
      this.option.get('percent').setErrors({'incorrect': true})
      this.isPercentInvalid = true;
      return false
    }
    return true
  }

  openChooseImageModal(type = 0) { // 0 for 2x1, 1 for 1x1
    const params = {
      start: 0,
      returnCount: 40,
      filterBy:[type]
    }

    this.campaignService.getOrganizationImages(params).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((imageList: any)=> {
      const list = imageList || [];
      const images = list.filter((image) => image.type === type)
      const modalRef = this.chooseImageModalService.open({ images, type });
    
      modalRef.onClose.pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((imageUrl) =>{
        if(imageUrl) {
          this.option.get(`imageDataUrl`).setValue(imageUrl);
          this.option.get(`image`).setValue(imageUrl);
          this.toastService.show('success', 'Added Successfully!', {delay: 2000});
        }
        this.chooseImageModalService.modalRef = null;
      })
    })
  }

  resetImage() {
    this.option.get('imageFile').setValue(null);
    this.option.get('imageDataUrl').setValue('/images/blank_2x1.png');
    this.option.get('image').setValue('/images/blank_2x1.png');
  }

  ngOnDestroy(): void {
    this.takeUntilSubject.next(null);
    this.takeUntilSubject.complete();
  }
}
